import { useState, useCallback } from "react";
import { Modal, ModalBody, ModalFooter } from "@/components/Modal";
import { CharacterCounter } from "@/components/CharacterCounter";
import { Tooltip } from "@/components/Tooltip";
import { AutoResizeTextarea } from "@/components/AutoResizeTextarea";
import { CategorySelector } from "@/components/CategorySelector";
import { textareaClass, fieldLabelClass } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import type { CriteriaCategory, GradeLevel } from "@markeroo/shared";
import { VoiceDictationButton } from "./VoiceDictationButton";
import { ui, placeholders } from "@/strings";
import PiArrowLeftStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiArrowLeftStroke";
import PiMicMicrophoneStroke from "@markeroo/icons/src/stroke/media/PiMicMicrophoneStroke";
import PiPencilEditBoxStroke from "@markeroo/icons/src/stroke/general/PiPencilEditBoxStroke";
import PiFileTextStroke from "@markeroo/icons/src/stroke/files-&-folders/PiFileTextStroke";

const MAX_INPUT_LENGTH = 20000; // ~5000 tokens
const WARNING_THRESHOLD = 18000; // 90% of max

type Step = "choose-method" | "voice" | "text" | "manual";

interface AddCriteriaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (prompt: string) => Promise<void> | void;
  onManualCreate: (data: { label: string; humanDescription: string; llmDescription: string; category: CriteriaCategory }) => Promise<void> | void;
  isGenerating?: boolean;
  isCreatingManual?: boolean;
  gradeLevel?: GradeLevel | null;
}

function getPlaceholderForGradeLevel(gradeLevel: GradeLevel | null | undefined): string {
  const level = gradeLevel ? parseInt(gradeLevel) : null;

  // Grades 3-5: Early/Upper Elementary
  if (level !== null && level >= 3 && level <= 5) {
    return `Describe your assignment or the skills you want students to focus on.

Example: "Write a 3-paragraph essay about an animal. Each paragraph needs a topic sentence and 3-4 supporting details."`;
  }

  // Grades 6-8: Middle School
  if (level !== null && level >= 6 && level <= 8) {
    return `Describe your assignment or the skills you want students to focus on.

Example: "Students should focus on strong thesis statements, using textual evidence, and addressing counterarguments."`;
  }

  // Grades 9-12: High School (default)
  return `Describe your assignment or the skills you want students to focus on.

Example: "Analyze the theme of power in Macbeth with at least 5 quotations demonstrating understanding of dramatic irony."`;
}

export function AddCriteriaModal({
  isOpen,
  onClose,
  onGenerate,
  onManualCreate,
  isGenerating = false,
  isCreatingManual = false,
  gradeLevel,
}: AddCriteriaModalProps) {
  const [currentStep, setCurrentStep] = useState<Step>("choose-method");

  // Text input state
  const [prompt, setPrompt] = useState("");
  const [activeMethod, setActiveMethod] = useState<"voice" | "text" | null>(null);
  const [lastTranscription, setLastTranscription] = useState<string>("");
  const [lastInputMethod, setLastInputMethod] = useState<"voice" | "text" | null>(null);

  // Manual form state
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [llmDescription, setLlmDescription] = useState("");
  const [category, setCategory] = useState<CriteriaCategory>("other");

  const isProcessing = isGenerating || isCreatingManual;

  const resetState = useCallback(() => {
    setCurrentStep("choose-method");
    setPrompt("");
    setActiveMethod(null);
    setLastTranscription("");
    setLastInputMethod(null);
    setLabel("");
    setDescription("");
    setLlmDescription("");
    setCategory("other");
  }, []);

  const handleClose = () => {
    if (isProcessing) return;
    resetState();
    onClose();
  };

  const handleBack = () => {
    if (isProcessing) return;
    setCurrentStep("choose-method");
  };

  const handleGenerate = async (text?: string) => {
    const value = (text || prompt).trim();
    if (!value || value.length > MAX_INPUT_LENGTH) {
      return;
    }
    setLastInputMethod("text");
    setActiveMethod("text");
    try {
      await onGenerate(value);
      // Success - reset and close
      resetState();
    } catch {
      // Text remains for retry
      setActiveMethod(null);
    }
  };

  const handleVoiceTranscription = useCallback(
    async (transcribedText: string) => {
      setLastTranscription(transcribedText);
      setLastInputMethod("voice");
      setActiveMethod("voice");
      try {
        await onGenerate(transcribedText);
        // Success - reset and close
        resetState();
      } catch {
        setActiveMethod(null);
      }
    },
    [onGenerate, resetState]
  );

  const handleRetry = async () => {
    if (lastInputMethod === "voice" && lastTranscription) {
      setActiveMethod("voice");
      try {
        await onGenerate(lastTranscription);
        resetState();
      } catch {
        setActiveMethod(null);
      }
    } else if (lastInputMethod === "text" && prompt) {
      await handleGenerate();
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;

    try {
      await onManualCreate({
        label: label.trim(),
        humanDescription: description.trim(),
        llmDescription: llmDescription.trim(),
        category,
      });
      // Success - reset and close
      resetState();
    } catch {
      // Form state preserved for retry
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void handleGenerate();
    }
  };

  const placeholder = getPlaceholderForGradeLevel(gradeLevel);

  // Header with optional back button
  const renderHeader = (title: string, showBack: boolean) => (
    <div className="flex items-center gap-3 flex-shrink-0">
      {showBack && (
        <button
          type="button"
          onClick={handleBack}
          disabled={isProcessing}
          className="p-1.5 -ml-1.5 rounded-lg text-text-theme-muted hover:text-text-theme-primary hover:bg-surface-secondary transition disabled:opacity-50"
          aria-label={ui.actions.back}
        >
          <PiArrowLeftStroke size={20} />
        </button>
      )}
      <h2 className="text-xl sm:text-2xl font-bold text-text-theme-primary pr-8">{title}</h2>
      <button
        onClick={handleClose}
        disabled={isProcessing}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 text-text-theme-muted hover:text-text-theme-secondary text-2xl leading-none p-0 w-6 h-6 flex items-center justify-center disabled:opacity-50"
        aria-label={ui.actions.close}
      >
        {/* eslint-disable-next-line no-restricted-syntax */}
        <span aria-hidden="true">✕</span>
      </button>
    </div>
  );

  // Step 1: Choose method
  const renderChooseMethod = () => (
    <>
      {renderHeader(ui.actions.addCriteria, false)}
      <ModalBody>
        <p className="text-text-theme-secondary mb-4">{ui.labels.chooseInputMethod}</p>
        <div className="flex flex-col gap-3" data-tutorial-target="add-criteria-input-methods">
          {/* Voice option - Intentional blue for voice input method */}
          {/* eslint-disable no-restricted-syntax */}
          <button
            type="button"
            onClick={() => setCurrentStep("voice")}
            className="flex items-center gap-4 p-4 rounded-xl border-2 border-border bg-white hover:border-blue-300 hover:bg-blue-50/50 transition text-left group"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition">
              <PiMicMicrophoneStroke size={24} className="text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-text-theme-primary">{ui.labels.speakYourCriteria}</div>
              <div className="text-sm text-text-theme-muted">{ui.labels.speakYourCriteriaDescription}</div>
            </div>
          </button>
          {/* eslint-enable no-restricted-syntax */}

          {/* Text option - Intentional green for text input method */}
          {/* eslint-disable no-restricted-syntax */}
          <button
            type="button"
            onClick={() => setCurrentStep("text")}
            className="flex items-center gap-4 p-4 rounded-xl border-2 border-border bg-white hover:border-green-300 hover:bg-green-50/50 transition text-left group"
            data-tutorial-target="text-method-option"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition">
              <PiFileTextStroke size={24} className="text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-text-theme-primary">{ui.labels.typeOrPaste}</div>
              <div className="text-sm text-text-theme-muted">{ui.labels.typeOrPasteDescription}</div>
            </div>
          </button>
          {/* eslint-enable no-restricted-syntax */}

          {/* Manual option - Intentional purple for manual input method */}
          {/* eslint-disable no-restricted-syntax */}
          <button
            type="button"
            onClick={() => setCurrentStep("manual")}
            className="flex items-center gap-4 p-4 rounded-xl border-2 border-border bg-white hover:border-purple-300 hover:bg-purple-50/50 transition text-left group"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition">
              <PiPencilEditBoxStroke size={24} className="text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-text-theme-primary">{ui.actions.createManually}</div>
              <div className="text-sm text-text-theme-muted">{ui.labels.createManuallyDescription}</div>
            </div>
          </button>
          {/* eslint-enable no-restricted-syntax */}
        </div>
      </ModalBody>
    </>
  );

  // Step 2a: Voice input
  const renderVoiceStep = () => (
    <>
      {renderHeader(ui.labels.speakYourCriteria, true)}
      <ModalBody>
        <div
          className={`flex-1 flex flex-col ${
            activeMethod === "voice" && isGenerating ? "shimmer-container" : ""
          }`}
        >
          <VoiceDictationButton
            onTranscriptionComplete={handleVoiceTranscription}
            isProcessing={isGenerating && activeMethod === "voice"}
          />
        </div>

        {/* Retry button */}
        {lastTranscription && !isGenerating && (
          <div className="flex justify-center mt-4">
            <button
              type="button"
              className={buttonVariants.secondary}
              onClick={() => { void handleRetry(); }}
            >
              {ui.actions.retryWithVoiceInput}
            </button>
          </div>
        )}
      </ModalBody>
    </>
  );

  // Step 2b: Text input
  const renderTextStep = () => (
    <>
      {renderHeader(ui.labels.typeOrPaste, true)}
      <ModalBody>
        <div
          className={`flex flex-col gap-3 ${
            activeMethod === "text" && isGenerating ? "shimmer-container p-4" : ""
          }`}
        >
          <div className="flex flex-col gap-2">
            <textarea
              className={`${textareaClass} resize-none`}
              placeholder={placeholder}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              data-tutorial-target="criteria-generator-textarea"
              rows={8}
              autoFocus
            />
            <CharacterCounter
              currentLength={prompt.length}
              maxLength={MAX_INPUT_LENGTH}
              warningThreshold={WARNING_THRESHOLD}
              className="text-right"
            />
          </div>
        </div>

        {/* Retry button */}
        {lastInputMethod === "text" && prompt && !isGenerating && (
          <div className="flex justify-center mt-4">
            <button
              type="button"
              className={buttonVariants.secondary}
              onClick={() => { void handleRetry(); }}
            >
              {ui.actions.retryWithTextInput}
            </button>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className={buttonVariants.secondary}
          onClick={handleBack}
          disabled={isGenerating}
        >
          {ui.actions.back}
        </button>
        <button
          type="button"
          className={buttonVariants.primary}
          onClick={() => { void handleGenerate(); }}
          disabled={isGenerating || !prompt.trim() || prompt.length > MAX_INPUT_LENGTH}
          data-tutorial-target="add-rules-button"
        >
          {isGenerating && activeMethod === "text" ? ui.actions.addingCriteria : ui.actions.addCriteria}
        </button>
      </ModalFooter>
    </>
  );

  // Step 2c: Manual form
  const renderManualStep = () => (
    <form onSubmit={(e) => { void handleManualSubmit(e); }} className="flex flex-col flex-1 min-h-0">
      {renderHeader(ui.actions.createManually, true)}
      <ModalBody>
        <div className="flex flex-col gap-4">
          <label className={fieldLabelClass}>
            <span className="flex items-center gap-1">
              {ui.labels.name}
              <Tooltip content={ui.labels.criterionNameHelp} />
            </span>
            <input
              type="text"
              className="rounded-lg border border-border bg-white px-3 py-2 text-base text-text-theme-primary shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              autoFocus
            />
          </label>

          <CategorySelector
            selectedCategory={category}
            onCategoryChange={setCategory}
          />

          <label className={fieldLabelClass}>
            <span className="flex items-center gap-1 text-text-theme-secondary">
              {ui.labels.studentFeedbackTemplate}
              <Tooltip content={ui.labels.criterionStudentHelp} />
            </span>
            <AutoResizeTextarea
              className="rounded-lg border border-border bg-white px-3 py-2 text-base text-text-theme-primary shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
              minHeight={80}
              maxHeight={200}
              placeholder={placeholders.criteria.humanDescription}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </label>

          <label className={fieldLabelClass}>
            <span className="flex items-center gap-1 text-text-theme-secondary">
              {ui.labels.llmInstructions}
              <Tooltip content={ui.labels.criterionLLMHelp} />
            </span>
            <AutoResizeTextarea
              className="rounded-lg border border-border bg-white px-3 py-2 text-base text-text-theme-primary shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
              minHeight={80}
              maxHeight={200}
              placeholder={placeholders.criteria.llmDescription}
              value={llmDescription}
              onChange={(e) => setLlmDescription(e.target.value)}
              rows={3}
            />
          </label>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className={buttonVariants.secondary}
          onClick={handleBack}
          disabled={isCreatingManual}
        >
          {ui.actions.back}
        </button>
        <button
          type="submit"
          className={buttonVariants.primary}
          disabled={isCreatingManual || !label.trim()}
        >
          {isCreatingManual ? ui.actions.saving : ui.actions.save}
        </button>
      </ModalFooter>
    </form>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      {currentStep === "choose-method" && renderChooseMethod()}
      {currentStep === "voice" && renderVoiceStep()}
      {currentStep === "text" && renderTextStep()}
      {currentStep === "manual" && renderManualStep()}
    </Modal>
  );
}
